(() => {
  'use strict';

  if (window.WUBHeaderV2?.version === '2.1-editor-safe') {
    window.WUBHeaderV2.init(document);
    return;
  }

  const DESKTOP_MIN_WIDTH = 1068;
  const controllers = new Map();

  class WubHeaderController {
    constructor(header) {
      this.header = header;
      this.root = document.documentElement;
      this.body = document.body;
      this.menuDrawer = header.querySelector('[data-wub-menu-drawer]');
      this.menuPanel = this.menuDrawer?.querySelector('.wub-menu-drawer__panel') || null;
      this.menuOpen = header.querySelector('[data-wub-menu-open]');
      this.menuClose = header.querySelectorAll('[data-wub-menu-close]');
      this.searchDrawer = document.getElementById('Search-Drawer');
      this.searchPanel = this.searchDrawer?.querySelector('.wub-search-drawer__inner') || this.searchDrawer;
      this.searchOpenButtons = header.querySelectorAll('[data-wub-search-open]');
      this.searchCloseButton = this.searchDrawer?.querySelector('[data-wub-search-drawer-close]');
      this.cartOpenButtons = header.querySelectorAll('[data-wub-cart-open]');
      this.cartCountNodes = header.querySelectorAll('[data-wub-cart-count]');
      this.desktopNav = header.querySelector('.wub-header__desktop-nav');
      this.desktopMedia = window.matchMedia(`(min-width: ${DESKTOP_MIN_WIDTH}px)`);

      this.lastMenuTrigger = null;
      this.lastSearchTrigger = null;
      this.measureFrame = 0;
      this.headerResizeObserver = null;
      this.removers = [];
      this.destroyed = false;

      this.publishHeaderOffset = this.publishHeaderOffset.bind(this);
      this.handleBreakpointChange = this.handleBreakpointChange.bind(this);
      this.syncDesktopNavFromFocus = this.syncDesktopNavFromFocus.bind(this);

      this.mount();
    }

    on(target, type, handler, options) {
      if (!target?.addEventListener) return;
      target.addEventListener(type, handler, options);
      this.removers.push(() => target.removeEventListener(type, handler, options));
    }

    mount() {
      const { header } = this;
      if (!header?.isConnected || header.dataset.wubHeaderReady === 'true') return;
      header.dataset.wubHeaderReady = 'true';

      this.menuDrawer?.setAttribute('inert', '');
      this.searchDrawer?.setAttribute('inert', '');

      this.on(this.menuOpen, 'click', (event) => {
        if (!this.menuDrawer || this.desktopMedia.matches) return;
        event.preventDefault();
        if (this.menuIsOpen()) this.closeMenu();
        else this.openMenu(this.menuOpen);
      });

      this.menuClose.forEach((control) => this.on(control, 'click', (event) => {
        if (!this.menuIsOpen()) return;
        event.preventDefault();
        this.closeMenu();
      }));

      this.searchOpenButtons.forEach((button) => this.on(button, 'click', (event) => {
        if (!this.searchDrawer) return;
        event.preventDefault();
        this.openSearch(button);
      }));

      this.on(this.searchCloseButton, 'click', (event) => {
        event.preventDefault();
        this.closeSearch();
      });

      this.on(this.searchDrawer, 'click', (event) => {
        if (event.target === this.searchDrawer) this.closeSearch(false);
      });

      this.cartOpenButtons.forEach((trigger) => this.on(trigger, 'click', (event) => {
        if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        if (this.tryOpenCart(trigger)) event.preventDefault();
      }));

      this.on(this.desktopNav, 'pointerover', (event) => {
        const owner = this.navOwnerFromTarget(event.target);
        if (!owner || owner.contains(event.relatedTarget)) return;
        owner.classList.remove('is-escape-closed');
        this.expandOwnerChain(owner, owner.classList.contains('is-keyboard-open'));
      });

      this.on(this.desktopNav, 'pointerout', (event) => {
        const owner = this.navOwnerFromTarget(event.target);
        if (!owner || owner.contains(event.relatedTarget)) return;
        if (owner.classList.contains('is-keyboard-open') || owner.contains(document.activeElement)) return;
        this.setOwnerExpanded(owner, false);
      });

      this.on(this.desktopNav, 'focusin', (event) => {
        const owner = this.navOwnerFromTarget(event.target);
        if (owner) this.expandOwnerChain(owner, owner.classList.contains('is-keyboard-open'));
      });

      this.on(this.desktopNav, 'focusout', () => {
        window.requestAnimationFrame(this.syncDesktopNavFromFocus);
      });

      this.on(this.desktopNav, 'keydown', (event) => {
        if (event.key === 'Escape') {
          this.handleDesktopNavEscape(event);
          return;
        }

        if (event.key !== ' ' && event.code !== 'Space') return;
        const toggle = event.target.closest?.('[data-wub-nav-toggle]');
        if (!toggle || !this.desktopNav.contains(toggle)) return;
        const owner = this.navOwnerForToggle(toggle);
        if (!owner) return;

        event.preventDefault();
        const expanded = toggle.getAttribute('aria-expanded') === 'true' && !owner.classList.contains('is-escape-closed');
        if (expanded) {
          this.closeOwnerTree(owner, { suppress: true });
        } else {
          owner.classList.remove('is-escape-closed');
          this.expandOwnerChain(owner, true);
        }
      });

      this.on(document, 'click', (event) => {
        if (this.desktopNav && !this.desktopNav.contains(event.target)) this.closeDesktopMenus();
      });

      this.on(document, 'keydown', (event) => {
        if (this.searchIsOpen()) {
          if (event.key === 'Escape') {
            event.preventDefault();
            this.closeSearch();
          } else {
            this.trapFocus(event, this.searchPanel);
          }
          return;
        }

        if (this.menuIsOpen()) {
          if (event.key === 'Escape') {
            event.preventDefault();
            this.closeMenu();
          } else {
            this.trapFocus(event, this.menuPanel);
          }
        }
      });

      this.on(document, 'wub:cart-drawer-opened', () => {
        this.closeMenu(false);
        this.closeSearch(false);
        this.closeDesktopMenus();
      });

      ['cart:updated', 'cart:update', 'cart:refresh', 'cart:item-added', 'line-item:change:end'].forEach((name) => {
        this.on(document, name, (event) => this.onCartEvent(event));
      });

      if (typeof this.desktopMedia.addEventListener === 'function') {
        this.on(this.desktopMedia, 'change', this.handleBreakpointChange);
      } else if (typeof this.desktopMedia.addListener === 'function') {
        const legacyHandler = this.handleBreakpointChange;
        this.desktopMedia.addListener(legacyHandler);
        this.removers.push(() => this.desktopMedia.removeListener?.(legacyHandler));
      }

      this.publishHeaderOffset();
      this.on(window, 'load', this.publishHeaderOffset, { once: true });

      if ('ResizeObserver' in window) {
        this.headerResizeObserver = new ResizeObserver(this.publishHeaderOffset);
        this.headerResizeObserver.observe(header);
      } else {
        this.on(window, 'resize', this.publishHeaderOffset, { passive: true });
      }
    }

    destroy() {
      if (this.destroyed) return;
      this.closeMenu(false);
      this.closeSearch(false);
      this.closeDesktopMenus();
      this.destroyed = true;
      window.cancelAnimationFrame(this.measureFrame);
      this.headerResizeObserver?.disconnect();
      this.headerResizeObserver = null;
      this.removers.splice(0).reverse().forEach((remove) => {
        try { remove(); } catch (_) {}
      });
      this.header?.removeAttribute('data-wub-header-ready');
      this.syncBodyLock();
    }

    focusableElements(container) {
      if (!container) return [];
      return Array.from(container.querySelectorAll(
        'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),summary,[tabindex]:not([tabindex="-1"])'
      )).filter((node) => {
        if (node.hidden || node.closest('[inert]')) return false;
        return node.getClientRects().length > 0;
      });
    }

    trapFocus(event, container) {
      if (event.key !== 'Tab' || !container) return;
      const focusable = this.focusableElements(container);
      if (!focusable.length) {
        event.preventDefault();
        container.focus?.({ preventScroll: true });
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (!container.contains(active)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus({ preventScroll: true });
      } else if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus({ preventScroll: true });
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus({ preventScroll: true });
      }
    }

    publishHeaderOffset() {
      window.cancelAnimationFrame(this.measureFrame);
      this.measureFrame = window.requestAnimationFrame(() => {
        if (this.destroyed || !this.header?.isConnected) return;
        const section = this.header.closest('.shopify-section-wub-header');
        const height = Math.round((section || this.header).getBoundingClientRect().height);
        if (!height) return;
        const value = `${height}px`;
        this.root.style.setProperty('--wub-header-offset', value);
        this.root.style.setProperty('--header-height', value);
      });
    }

    menuIsOpen() {
      return this.header.classList.contains('is-menu-open');
    }

    searchIsOpen() {
      return Boolean(this.searchDrawer?.classList.contains('active'));
    }

    cartIsOpen() {
      return Boolean(document.getElementById('WubCartDrawer')?.classList.contains('is-open'));
    }

    syncBodyLock() {
      if (!this.body) return;
      const shouldLock = !this.destroyed && (this.menuIsOpen() || this.searchIsOpen());
      this.body.classList.toggle('wub-shell-locked', shouldLock);
    }

    closeCartDrawer() {
      if (!this.cartIsOpen()) return;
      const commerce = window.WUBCommerce;
      if (commerce && typeof commerce.closeDrawer === 'function') {
        commerce.closeDrawer({ restoreFocus: false });
      }
    }

    closeMenu(returnFocus = true) {
      if (!this.menuDrawer || !this.menuIsOpen()) return;
      this.header.classList.remove('is-menu-open');
      this.menuDrawer.setAttribute('aria-hidden', 'true');
      this.menuDrawer.setAttribute('inert', '');
      this.menuOpen?.setAttribute('aria-expanded', 'false');
      this.syncBodyLock();
      if (returnFocus && this.lastMenuTrigger?.isConnected && typeof this.lastMenuTrigger.focus === 'function') {
        window.requestAnimationFrame(() => this.lastMenuTrigger.focus({ preventScroll: true }));
      }
    }

    openMenu(trigger) {
      if (!this.menuDrawer || this.desktopMedia.matches) return false;
      this.closeSearch(false);
      this.closeDesktopMenus();
      this.closeCartDrawer();
      this.lastMenuTrigger = trigger || document.activeElement;
      this.header.classList.add('is-menu-open');
      this.menuDrawer.removeAttribute('inert');
      this.menuDrawer.setAttribute('aria-hidden', 'false');
      this.menuOpen?.setAttribute('aria-expanded', 'true');
      this.syncBodyLock();
      const firstFocusable = this.focusableElements(this.menuPanel)[0];
      if (firstFocusable) window.requestAnimationFrame(() => firstFocusable.focus({ preventScroll: true }));
      return true;
    }

    closeSearch(returnFocus = true) {
      if (!this.searchDrawer || !this.searchIsOpen()) return;
      this.searchDrawer.classList.remove('active');
      this.searchDrawer.setAttribute('inert', '');
      this.searchDrawer.setAttribute('aria-hidden', 'true');
      this.searchOpenButtons.forEach((button) => button.setAttribute('aria-expanded', 'false'));
      this.syncBodyLock();
      if (returnFocus && this.lastSearchTrigger?.isConnected && typeof this.lastSearchTrigger.focus === 'function') {
        window.requestAnimationFrame(() => this.lastSearchTrigger.focus({ preventScroll: true }));
      }
    }

    openSearch(trigger) {
      if (!this.searchDrawer) return false;
      this.closeMenu(false);
      this.closeDesktopMenus();
      this.closeCartDrawer();
      this.lastSearchTrigger = trigger || document.activeElement;
      this.searchDrawer.classList.add('active');
      this.searchDrawer.removeAttribute('inert');
      this.searchDrawer.setAttribute('aria-hidden', 'false');
      this.searchOpenButtons.forEach((button) => button.setAttribute('aria-expanded', 'true'));
      this.syncBodyLock();
      const input = this.searchDrawer.querySelector('input[type="search"]');
      const firstFocusable = input || this.focusableElements(this.searchPanel)[0];
      if (firstFocusable) window.requestAnimationFrame(() => firstFocusable.focus({ preventScroll: true }));
      return true;
    }

    navOwnerForToggle(toggle) {
      return toggle?.closest('.wub-nav__dropdown-item.has-children, .wub-nav__item.has-children') || null;
    }

    directNavToggle(owner) {
      if (!owner) return null;
      return Array.from(owner.children).find((child) => child.matches?.('[data-wub-nav-toggle]')) || null;
    }

    topNavOwner(owner) {
      return owner?.closest('.wub-nav__item.has-children') || null;
    }

    setOwnerExpanded(owner, expanded, keyboard = false) {
      const toggle = this.directNavToggle(owner);
      if (!toggle) return;
      toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      if (expanded) {
        owner.classList.remove('is-escape-closed');
        owner.classList.toggle('is-keyboard-open', keyboard);
      } else {
        owner.classList.remove('is-keyboard-open');
      }
    }

    closeOwnerTree(owner, { suppress = false } = {}) {
      if (!owner) return;
      const owners = [owner, ...owner.querySelectorAll('.wub-nav__dropdown-item.has-children')];
      owners.forEach((item) => {
        const toggle = this.directNavToggle(item);
        toggle?.setAttribute('aria-expanded', 'false');
        item.classList.remove('is-keyboard-open');
        if (item !== owner) item.classList.remove('is-escape-closed');
      });
      owner.classList.toggle('is-escape-closed', suppress);
    }

    closeDesktopMenus() {
      if (!this.desktopNav) return;
      this.desktopNav.querySelectorAll('.wub-nav__item.has-children').forEach((item) => this.closeOwnerTree(item));
    }

    closeTopNavSiblings(activeTop) {
      if (!this.desktopNav) return;
      this.desktopNav.querySelectorAll('.wub-nav__item.has-children').forEach((item) => {
        if (item !== activeTop) this.closeOwnerTree(item);
      });
    }

    expandOwnerChain(owner, keyboard = false) {
      if (!owner || !this.desktopNav) return;
      const activeTop = this.topNavOwner(owner);
      this.closeTopNavSiblings(activeTop);

      let current = owner;
      let first = true;
      while (current && this.desktopNav.contains(current)) {
        if (!current.classList.contains('is-escape-closed')) {
          this.setOwnerExpanded(current, true, keyboard && first);
        }
        first = false;
        const parent = current.parentElement;
        current = parent?.closest('.wub-nav__dropdown-item.has-children, .wub-nav__item.has-children') || null;
      }
    }

    navOwnerFromTarget(target) {
      if (!(target instanceof Element)) return null;
      const owner = target.closest('.wub-nav__dropdown-item.has-children, .wub-nav__item.has-children');
      return owner && this.desktopNav?.contains(owner) ? owner : null;
    }

    syncDesktopNavFromFocus() {
      if (!this.desktopNav) return;
      const active = document.activeElement;

      this.desktopNav.querySelectorAll('.is-escape-closed').forEach((owner) => {
        if (!owner.contains(active)) owner.classList.remove('is-escape-closed');
      });

      if (!(active instanceof Element) || !this.desktopNav.contains(active)) {
        this.closeDesktopMenus();
        return;
      }

      const owner = this.navOwnerFromTarget(active);
      if (owner) this.expandOwnerChain(owner, false);
    }

    handleDesktopNavEscape(event) {
      if (!this.desktopNav || !(event.target instanceof Element) || !this.desktopNav.contains(event.target)) return false;

      let owner = this.navOwnerFromTarget(event.target);
      while (owner) {
        const toggle = this.directNavToggle(owner);
        if (toggle?.getAttribute('aria-expanded') === 'true') {
          event.preventDefault();
          event.stopPropagation();
          this.closeOwnerTree(owner, { suppress: true });
          toggle.focus({ preventScroll: true });
          return true;
        }
        const parent = owner.parentElement;
        owner = parent?.closest('.wub-nav__dropdown-item.has-children, .wub-nav__item.has-children') || null;
      }
      return false;
    }

    async fetchCartCount() {
      if (!this.cartCountNodes.length) return;
      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
          cache: 'no-store'
        });
        if (!response.ok) return;
        const cart = await response.json();
        this.updateCartCount(Number(cart.item_count || 0));
      } catch (_) {}
    }

    updateCartCount(count) {
      if (!Number.isFinite(count)) return;
      this.cartCountNodes.forEach((node) => {
        node.textContent = String(count);
        node.toggleAttribute('hidden', count < 1);
      });
    }

    onCartEvent(event) {
      const count = Number(event?.detail?.cart?.item_count ?? event?.detail?.item_count);
      if (Number.isFinite(count)) this.updateCartCount(count);
      else this.fetchCartCount();
    }

    tryOpenCart(trigger) {
      const commerce = window.WUBCommerce;
      if (!commerce || typeof commerce.openDrawer !== 'function') return false;
      this.closeMenu(false);
      this.closeSearch(false);
      this.closeDesktopMenus();
      return commerce.openDrawer({ trigger }) === true;
    }

    handleBreakpointChange(event) {
      if (event.matches) {
        this.closeMenu(false);
        this.menuDrawer?.querySelectorAll('details[open]').forEach((details) => details.removeAttribute('open'));
      } else {
        this.closeDesktopMenus();
      }
      this.publishHeaderOffset();
    }
  }

  function headersIn(root = document) {
    if (!root) return [];
    const matches = root.matches?.('[data-wub-header]') ? [root] : [];
    return matches.concat(Array.from(root.querySelectorAll?.('[data-wub-header]') || []));
  }

  function prune() {
    controllers.forEach((controller, header) => {
      if (!header.isConnected) {
        controller.destroy();
        controllers.delete(header);
      }
    });
  }

  function init(root = document) {
    prune();
    headersIn(root).forEach((header) => {
      if (controllers.has(header)) return;
      if (header.dataset.wubHeaderReady === 'true') header.removeAttribute('data-wub-header-ready');
      const controller = new WubHeaderController(header);
      if (!controller.destroyed && header.dataset.wubHeaderReady === 'true') controllers.set(header, controller);
    });
  }

  function destroy(root = document) {
    headersIn(root).forEach((header) => {
      const controller = controllers.get(header);
      if (!controller) return;
      controller.destroy();
      controllers.delete(header);
    });
    prune();
  }

  window.WUBHeaderV2 = Object.freeze({
    version: '2.1-editor-safe',
    init,
    destroy
  });

  document.addEventListener('shopify:section:unload', (event) => destroy(event.target));
  document.addEventListener('shopify:section:load', (event) => init(event.target));

  init(document);
})();
